import { useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useRouteParams } from '@/contexts/RouteContext';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { useGo } from '@/hooks/useResourceNavigation';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';

import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import type {
	PricingPolicyQuery,
	UpdatePricingPolicyMutation,
	UpdatePricingPolicyMutationVariables
} from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { PricingPolicyForm } from './pricing-policy-form';
import {
	buildUpdatePricingPolicyInput,
	pricingPolicyFormSchema,
	type PricingPolicyFormValues
} from './pricing-policy-input';
import { PRICING_POLICY_QUERY, PRICING_POLICIES_LIST_QUERY } from './pricing-policies.gql';
import { PRICING_POLICY_UPDATE_MUTATION } from './pricing-policy-update.gql';
import { BILLING_CYCLES_LIST_QUERY } from './lookup.gql';

const isValidUlid = (value: string | undefined): value is string => {
	if (!value) {
		return false;
	}

	return /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i.test(value);
};

export const PricingPoliciesEdit = () => {
	const { id } = useRouteParams();
	const go = useGo();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const {
		data: policyData,
		loading: loadingPolicy,
		error: policyError
	} = useQuery<PricingPolicyQuery>(PRICING_POLICY_QUERY, {
		variables: { id: id ?? '' },
		skip: !isValidUlid(id)
	});

	const policy = policyData?.pricingPolicy ?? null;
	const memoizedInitialValues = useMemo<
		PricingPolicyFormValues | undefined
	>(() => {
		if (!policy) {
			return undefined;
		}

		return {
			name: policy.name ?? '',
			priority: policy.priority ? String(policy.priority) : '',
			isActive: Boolean(policy.isActive),
			scenarioId: policy.scenario?.scenarioId ?? '',
			tariffId: policy.tariff?.tariffId ?? '',
			overstayTariffId: policy.overstayTariff?.tariffId ?? '',
			limitId: policy.limit?.limitId ?? '',
			billingCycleId: policy.billingCycle?.billingCycleId
				? String(policy.billingCycle.billingCycleId)
				: ''
		};
	}, [policy]);

	const initialLabels = useMemo(
		() => ({
			tariffLabel: policy?.tariff?.name,
			overstayTariffLabel: policy?.overstayTariff?.name,
			limitLabel: policy?.limit?.name,
			scenarioLabel: policy?.scenario?.name
		}),
		[policy]
	);


	const form = useEntityForm<PricingPolicyFormValues>({
		mode: 'edit',
		schema: pricingPolicyFormSchema,
		defaultValues: {
			name: '',
			priority: '',
			isActive: true,
			scenarioId: '',
			tariffId: '',
			overstayTariffId: '',
			limitId: '',
			billingCycleId: ''
		},
		initialData: memoizedInitialValues
	});

	const {
		data: billingCyclesData,
		loading: loadingBillingCycles,
		error: billingCyclesError
	} = useQuery(BILLING_CYCLES_LIST_QUERY);

	const { data: pricingPoliciesData } = useQuery(PRICING_POLICIES_LIST_QUERY, {
		variables: { first: 100 }
	});

	const lookupsLoading = loadingBillingCycles;
	const lookupError = billingCyclesError;

	// Use delayed loading to prevent flashing "Unable to load form data" message
	const { shouldShowLoading } = useDelayedLoading(lookupsLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const billingCycles = billingCyclesData?.billingCycles ?? [];

	const existingPriorities = useMemo(() => {
		const nodes = pricingPoliciesData?.pricingPolicies?.nodes ?? [];
		return nodes
			.filter((node: any) => node.priority != null && node.pricingPolicyId !== id)
			.map((node: any) => ({
				priority: node.priority,
				label: node.name
			}));
	}, [pricingPoliciesData, id]);

	const [updatePricingPolicy, { loading: isSubmitting }] = useMutation<
		UpdatePricingPolicyMutation,
		UpdatePricingPolicyMutationVariables
	>(PRICING_POLICY_UPDATE_MUTATION);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	// Set up page header
	useEffect(() => {
		setPageHeader({
			title: 'Edit Pricing Policy',
			description: 'Update the pricing policy details',
			showBackButton: true,
			onBackClick: () => go({ to: '/pricing-policies' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go]);

	const handleSubmit = async (values: PricingPolicyFormValues) => {
		if (!policy?.pricingPolicyId) {
			showMutationErrorToast('pricing-policies', 'update', { message: 'Pricing policy identifier is missing.' });
			return;
		}

		let input;

		try {
			input = buildUpdatePricingPolicyInput(
				values,
				policy.pricingPolicyId
			);
		} catch (error) {
			showMutationErrorToast('pricing-policies', 'update', { error });
			return;
		}

		try {
			const { data } = await updatePricingPolicy(
				getMutationOptions('pricing-policies', { input })
			);

			const message = extractCheckResultError(data?.updatePricingPolicy);

			if (message) {
				showMutationErrorToast('pricing-policies', 'update', { message });
				return;
			}

			showMutationSuccessToast('pricing-policies', 'update');
			go({
				to: `/pricing-policies?success=${policy.pricingPolicyId}&mode=edit`
			});
		} catch (error) {
			showMutationErrorToast('pricing-policies', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Pricing policy not found</AlertTitle>
					<AlertDescription>
						A pricing policy identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid(id)) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Invalid pricing policy ID</AlertTitle>
					<AlertDescription>
						The provided identifier "{id}" is not a valid ULID.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (loadingPolicy) {
		return (
			<Card className="text-sm text-muted-foreground">
				Loading pricing policy...
			</Card>
		);
	}

	if (policyError) {
		return (
			<Alert variant="destructive">
				<AlertTitle>Unable to load pricing policy</AlertTitle>
				<AlertDescription>
					{policyError.message ??
						'The requested pricing policy could not be loaded.'}
				</AlertDescription>
			</Alert>
		);
	}

	if (!policy) {
		return (
			<Alert variant="destructive">
				<AlertTitle>Pricing policy not found</AlertTitle>
				<AlertDescription>
					The requested pricing policy could not be found.
				</AlertDescription>
			</Alert>
		);
	}

	return (
		<FormPageLayout>
			{lookupError && !shouldShowLoading && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load form data</AlertTitle>
					<AlertDescription>
						{lookupError.message ??
							'Some lookup values could not be loaded.'}
					</AlertDescription>
				</Alert>
			)}

			{shouldShowLoading ? (
				<Card>
					<div className="text-sm text-muted-foreground">
						Loading form data...
					</div>
				</Card>
			) : (
				<PricingPolicyForm
					key={id} // Force remount when editing different policies to ensure clean state
					mode="edit"
					form={form}
					billingCycles={billingCycles}
					existingPriorities={existingPriorities}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					initialTariffLabel={initialLabels.tariffLabel}
					initialOverstayTariffLabel={
						initialLabels.overstayTariffLabel
					}
					initialLimitLabel={initialLabels.limitLabel}
					initialScenarioLabel={initialLabels.scenarioLabel}
					headerTitle="Edit Pricing Policy"
				/>
			)}
		</FormPageLayout>
	);
};
